import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AttachmentRef, ChatError, ChatMessage, ToolCallView } from '../types';
import type { ChatRuntime, ChatRuntimeSnapshot, SendInput, UseChatRuntime } from './types';
import { toSnapshot } from './toSnapshot';

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `mock-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const MOCK_REPLY_PARTS = [
  'Thanks for the message — this is a mocked assistant response. ',
  'Once the chat backend is wired up, this will be replaced by a real model response. ',
  'For now, the UI is exercised end-to-end so layout, streaming, and error states can be reviewed.'
];

const MOCK_TOOL_CALL_NAME = 'mock_lookup_contracts';
const MOCK_TOOL_CALL_RESULT = 'Found 3 contracts matching the query.';
const STREAM_STEP_MS = 80;
const TOOL_CALL_RESOLVE_MS = 120;

interface MockTimer {
  cancel: () => void;
}

const scheduleSequence = (steps: Array<() => void>, stepMs: number): MockTimer => {
  const timeouts: ReturnType<typeof setTimeout>[] = [];
  let cancelled = false;
  for (let i = 0; i < steps.length; i++) {
    const t = setTimeout(() => {
      if (cancelled) return;
      steps[i]();
    }, stepMs * (i + 1));
    timeouts.push(t);
  }
  return {
    cancel: () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    }
  };
};

/**
 * In-process mock runtime used in Phase 1 dev/test runs and as the default
 * `ChatRuntimeContext` value. Owns its own message list in `useState` and
 * simulates a streamed assistant response with a single tool call so the
 * `ChatToolCallChip` rendering path is exercised end-to-end.
 *
 * Has no dependency on CopilotKit or AG-UI; the production adapter
 * (`useCopilotKitRuntime`) lands in Phase 2 and replaces this hook at the
 * provider mount.
 * @returns a `ChatRuntime` whose snapshot updates each render.
 */
export const useMockRuntime: UseChatRuntime = (): ChatRuntime => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [phase, setPhase] = useState<'idle' | 'thinking' | 'streaming'>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [lastError, setLastError] = useState<ChatError | null>(null);
  const activeTimerRef = useRef<MockTimer | null>(null);
  const activeAssistantIdRef = useRef<string | null>(null);
  const activeUserIdRef = useRef<string | null>(null);

  const finalizeStream = useCallback(() => {
    setPhase('idle');
    setRunId(null);
    activeTimerRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    activeTimerRef.current?.cancel();
    activeTimerRef.current = null;
    const userId = activeUserIdRef.current;
    const assistantId = activeAssistantIdRef.current;
    setMessages((prev) =>
      prev.map((m) => {
        if (assistantId && m.id === assistantId && m.status === 'streaming') {
          return { ...m, status: 'sent' };
        }
        if (userId && m.id === userId && m.status === 'pending') {
          return { ...m, status: 'sent' };
        }
        return m;
      })
    );
    activeAssistantIdRef.current = null;
    activeUserIdRef.current = null;
    setPhase('idle');
    setRunId(null);
  }, []);

  const startStreamFor = useCallback(
    (userId: string) => {
      activeTimerRef.current?.cancel();
      const assistantId = newId();
      const toolCallId = newId();
      activeAssistantIdRef.current = assistantId;
      activeUserIdRef.current = userId;
      const newRunId = newId();
      setRunId(newRunId);
      setPhase('thinking');
      setLastError(null);

      const placeholder: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        text: '',
        createdAt: new Date().toISOString(),
        status: 'streaming',
        toolCalls: [
          {
            id: toolCallId,
            name: MOCK_TOOL_CALL_NAME,
            status: 'running'
          }
        ]
      };
      setMessages((prev) => [...prev, placeholder]);

      const updateAssistant = (mutate: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? mutate(m) : m)));

      const updateToolCall = (mutate: (t: ToolCallView) => ToolCallView) =>
        updateAssistant((m) => ({
          ...m,
          toolCalls: m.toolCalls?.map((t) => (t.id === toolCallId ? mutate(t) : t))
        }));

      const steps: Array<() => void> = [];
      // Step 1: resolve tool call → done
      steps.push(() => {
        updateToolCall((t) => ({ ...t, status: 'done', resultSummary: MOCK_TOOL_CALL_RESULT }));
      });
      // Stream text deltas
      for (let i = 0; i < MOCK_REPLY_PARTS.length; i++) {
        const part = MOCK_REPLY_PARTS[i];
        const isFinalPart = i === MOCK_REPLY_PARTS.length - 1;
        const isFirstPart = i === 0;
        steps.push(() => {
          if (isFirstPart) setPhase('streaming');
          updateAssistant((m) => ({
            ...m,
            text: m.text + part,
            status: isFinalPart ? 'sent' : 'streaming'
          }));
          if (isFinalPart) {
            setMessages((prev) => prev.map((m) => (m.id === userId ? { ...m, status: 'sent' } : m)));
            finalizeStream();
            activeAssistantIdRef.current = null;
            activeUserIdRef.current = null;
          }
        });
      }

      const stepDelays = [TOOL_CALL_RESOLVE_MS, ...MOCK_REPLY_PARTS.map(() => STREAM_STEP_MS)];
      const timeouts: ReturnType<typeof setTimeout>[] = [];
      let cancelled = false;
      let elapsed = 0;
      for (let i = 0; i < steps.length; i++) {
        elapsed += stepDelays[i];
        const t = setTimeout(() => {
          if (cancelled) return;
          steps[i]();
        }, elapsed);
        timeouts.push(t);
      }
      activeTimerRef.current = {
        cancel: () => {
          cancelled = true;
          timeouts.forEach(clearTimeout);
        }
      };
    },
    [finalizeStream]
  );

  const send = useCallback(
    async ({ text }: SendInput): Promise<void> => {
      const userId = newId();
      const userMessage: ChatMessage = {
        id: userId,
        role: 'user',
        text,
        createdAt: new Date().toISOString(),
        status: 'pending'
      };
      setMessages((prev) => [...prev, userMessage]);
      startStreamFor(userId);
    },
    [startStreamFor]
  );

  const retry = useCallback(
    async (messageId: string): Promise<void> => {
      let target: ChatMessage | undefined;
      setMessages((prev) => {
        target = prev.find((m) => m.id === messageId && m.role === 'user');
        if (!target) return prev;
        return prev.map((m) => (m.id === messageId ? { ...m, status: 'pending', errorMessage: undefined } : m));
      });
      if (!target) return;
      startStreamFor(messageId);
    },
    [startStreamFor]
  );

  const uploadAttachment = useCallback(async (file: File): Promise<AttachmentRef> => {
    await new Promise((resolve) => setTimeout(resolve, 50));
    return {
      ref: `mock-ref-${newId()}`,
      name: file.name,
      sizeBytes: file.size,
      mimeType: file.type || 'application/octet-stream'
    };
  }, []);

  // Cancel any in-flight mock stream when the consuming component unmounts.
  useEffect(
    () => () => {
      activeTimerRef.current?.cancel();
      activeTimerRef.current = null;
    },
    []
  );

  const resetThread = useCallback(() => {
    activeTimerRef.current?.cancel();
    activeTimerRef.current = null;
    setMessages([]);
    setPhase('idle');
    setRunId(null);
    setLastError(null);
  }, []);

  const snapshot: ChatRuntimeSnapshot = useMemo(
    () => toSnapshot(messages, phase, runId, lastError),
    [messages, phase, runId, lastError]
  );

  return useMemo<ChatRuntime>(
    () => ({
      snapshot,
      send,
      cancel,
      retry,
      resetThread,
      uploadAttachment
    }),
    [snapshot, send, cancel, retry, resetThread, uploadAttachment]
  );
};
